import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Typography,
  Button,
  Alert
} from '@mui/material';
import { usePlanosPagamento } from '../../contexts/PlanosPagamentoContext';

import InformacoesBasicas from '../../pages/PlanosPagamento/Steps/InformacoesBasicas';
import ConfiguracaoPrestacoes from '../../pages/PlanosPagamento/Steps/ConfiguracaoPrestacoes';
import ConfiguracaoMetodosPagamento from '../../pages/PlanosPagamento/Steps/ConfiguracaoMetodosPagamento';
import Confirmacao from '../../pages/PlanosPagamento/Steps/Confirmacao';

const steps = [
  'Informações Básicas',
  'Configuração das Prestações',
  'Métodos de Pagamento',
  'Confirmação'
];

const CriarPlanoPagamento = () => {
  const navigate = useNavigate();
  const { criarPlanoPagamento } = usePlanosPagamento();
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    anoLetivo: '',
    descricao: '',
    valorTotal: '',
    dataInicio: null,
    dataFim: null,
    tipo: 'BASE', // Adicionar tipo
    prestacoes: [],
    regrasMulta: [
      { diasAtraso: 5, percentualMulta: 20, descricao: 'Multa até 5 dias' },
      { diasAtraso: 10, percentualMulta: 50, descricao: 'Multa até 10 dias' },
      { diasAtraso: 30, percentualMulta: 100, descricao: 'Multa após 10 dias' }
    ],
    metodosDisponiveisJSON: {
      MPESA: false,
      EMOLA: false,
      BIM: false,
      BCI: false,
      MOZABANK: false
    }
  });

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0: // Informações Básicas
        if (!formData.anoLetivo) newErrors.anoLetivo = 'Ano letivo é obrigatório';
        if (!formData.descricao) newErrors.descricao = 'Descrição é obrigatória';
        if (!formData.valorTotal) newErrors.valorTotal = 'Valor total é obrigatório';
        if (!formData.dataInicio) newErrors.dataInicio = 'Data de início é obrigatória';
        if (!formData.dataFim) newErrors.dataFim = 'Data de fim é obrigatória';
        if (formData.dataInicio && formData.dataFim && formData.dataInicio >= formData.dataFim) {
          newErrors.dataFim = 'Data de fim deve ser posterior à data de início';
        }
        break;

      case 1: // Configuração das Prestações
        if (formData.prestacoes.length === 0) {
          newErrors.prestacoes = 'Configure pelo menos uma prestação';
        }
        const totalPrestacoes = formData.prestacoes.reduce((sum, p) => sum + Number(p.valor), 0);
        if (Math.abs(totalPrestacoes - formData.valorTotal) > 0.01) {
          newErrors.prestacoes = 'O valor total das prestações deve ser igual ao valor total do plano';
        }
        break;

      case 2: // Métodos de Pagamento
        if (!Object.values(formData.metodosDisponiveisJSON).some(v => v)) {
          newErrors.metodosPagamento = 'Selecione pelo menos um método de pagamento';
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setErrors({});
  };

  const handleUpdateForm = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
    setErrors({});
  };

  const handleSubmit = async () => {
    try {
      if (!validateStep(activeStep)) {
        return;
      }

      await criarPlanoPagamento(formData);
      navigate('/planos-pagamento');
    } catch (error) {
      console.error('Erro ao criar plano:', error);
      setErrors({ submit: error.message });
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <InformacoesBasicas
            formData={formData}
            onUpdateForm={handleUpdateForm}
            errors={errors}
          />
        );
      case 1:
        return (
          <ConfiguracaoPrestacoes
            formData={formData}
            onUpdateForm={handleUpdateForm}
            errors={errors}
          />
        );
      case 2:
        return (
          <ConfiguracaoMetodosPagamento
            formData={formData}
            onUpdateForm={handleUpdateForm}
            errors={errors}
          />
        );
      case 3:
        return (
          <Confirmacao
            formData={formData}
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Criar Plano de Pagamento
      </Typography>

      <Box sx={{ mb: 3 }}>
  <Alert severity="info">
    Este plano será um template (plano base) e servirá como modelo para criar 
    planos individuais para cada estudante. Quando um estudante for vinculado, 
    será criado um plano individual baseado nesta configuração.
  </Alert>
</Box>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card>
        <CardContent>
          {errors.submit && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.submit}
            </Alert>
          )}

          {renderStepContent(activeStep)}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Box>
              {activeStep > 0 && (
                <Button onClick={handleBack}>
                  Voltar
                </Button>
              )}
            </Box>
            <Box>
              {activeStep < steps.length - 1 ? (
                <Button variant="contained" onClick={handleNext}>
                  Próximo
                </Button>
              ) : (
                <Button variant="contained" color="primary" onClick={handleSubmit}>
                  Criar Plano
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CriarPlanoPagamento;